import { supabase } from "@/integrations/supabase/client";

const OFFER_NEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export type StudentOfferStatus = "active" | "update_window_closed" | "closed_to_new_students";

export type StudentOfferPriority = "use_first" | "ready_now" | "still_delivering" | "settled";

export type StudentOfferNextActionKind = "open_qr_ready" | "open_new_files" | "open_offer" | "track_delivery";

export type StudentOfferSummary = {
  purchaseId: string;
  offerId: string;
  title: string;
  libraryId: string;
  libraryName: string | null;
  libraryUsername: string | null;
  purchasedAt: string;
  price: number;
  currency: string;
  periodLabel: string | null;
  startsAt: string | null;
  endsAt: string | null;
  promiseCount: number | null;
  deliveredCount: number;
  remainingCount: number | null;
  newCount: number;
  qrResourceCount: number;
  readyQrCount: number;
  recentResourceIds: string[];
  status: StudentOfferStatus;
  priority: StudentOfferPriority;
  nextActionKind: StudentOfferNextActionKind;
};

export type StudentOfferResource = {
  resourceId: string;
  title: string;
  category: string | null;
  yearLevel: number | null;
  addedAt: string;
  isBonus: boolean;
  isNew: boolean;
  thumbnailUrl?: string | null;
  thumbKey?: string | null;
  format?: string | null;
  includesPrintQrCode?: boolean;
  qrToken?: string | null;
  qrRedeemedAt?: string | null;
  libraryName: string | null;
  libraryUsername?: string | null;
};

export type StudentOfferDetail = {
  summary: StudentOfferSummary;
  description: string | null;
  offerHeadline: string | null;
  whatComingLater: string[];
  bonusItems: string[];
  requestId: string | null;
  requestCode: string | null;
  requestStatus: string | null;
  resources: StudentOfferResource[];
};

export type StudentOfferGroups = {
  useFirst: StudentOfferSummary[];
  readyNow: StudentOfferSummary[];
  stillDelivering: StudentOfferSummary[];
  settled: StudentOfferSummary[];
};

export type StudentOfferOwnership = {
  resourceId: string;
  offerId: string;
  title: string;
  purchasedAt: string;
  libraryName: string | null;
  libraryUsername: string | null;
  deliveredCount: number;
  promiseCount: number | null;
  remainingCount: number | null;
  newCount: number;
  status: StudentOfferStatus;
  owned: boolean;
};

type StudentOfferSummarySeed = Omit<StudentOfferSummary, "priority" | "nextActionKind">;

type BundlePurchaseRow = {
  id: string;
  bundle_id: string;
  purchased_at: string;
  bundles?:
    | {
        id: string;
        title: string;
        price: number | null;
        currency: string | null;
        promise_count: number | null;
        period_label: string | null;
        starts_at: string | null;
        ends_at: string | null;
        status: string | null;
        description?: string | null;
        offer_headline?: string | null;
        what_coming_later?: string[] | null;
        bonus_items?: string[] | null;
        library_id: string;
        libraries?:
          | {
              name: string | null;
              username: string | null;
            }
          | {
              name: string | null;
              username: string | null;
            }[]
          | null;
      }
    | {
        id: string;
        title: string;
        price: number | null;
        currency: string | null;
        promise_count: number | null;
        period_label: string | null;
        starts_at: string | null;
        ends_at: string | null;
        status: string | null;
        description?: string | null;
        offer_headline?: string | null;
        what_coming_later?: string[] | null;
        bonus_items?: string[] | null;
        library_id: string;
        libraries?:
          | {
              name: string | null;
              username: string | null;
            }
          | {
              name: string | null;
              username: string | null;
            }[]
          | null;
      }[]
    | null;
};

type BundleResourcesRow = {
  bundle_id: string | null;
  added_at: string | null;
  is_bonus?: boolean | null;
  resource_id: string | null;
  resources?:
    | {
        id: string;
        title: string;
        target_faculty: string | null;
        target_year: number | null;
        thumb_key: string | null;
        format: string | null;
        includes_print_qr_code?: boolean | null;
        library?:
          | {
              name: string | null;
              username: string | null;
            }
          | {
              name: string | null;
              username: string | null;
            }[]
          | null;
      }
    | {
        id: string;
        title: string;
        target_faculty: string | null;
        target_year: number | null;
        thumb_key: string | null;
        format: string | null;
        includes_print_qr_code?: boolean | null;
        library?:
          | {
              name: string | null;
              username: string | null;
            }
          | {
              name: string | null;
              username: string | null;
            }[]
          | null;
      }[]
    | null;
};

type GrantQrRow = {
  resource_id: string;
  source_bundle_id: string | null;
  qr_token: string | null;
  qr_redeemed_at: string | null;
};

type AccessRequestRow = {
  id: string;
  request_code: string | null;
  status: string;
};

const toArrayFirst = <T,>(value: T | T[] | null | undefined): T | null =>
  Array.isArray(value) ? (value[0] ?? null) : (value ?? null);

const normalizeOfferStatus = (bundleStatus: string | null | undefined, endsAt: string | null | undefined): StudentOfferStatus => {
  if (endsAt) {
    const endsAtMs = new Date(endsAt).getTime();
    if (Number.isFinite(endsAtMs) && endsAtMs < Date.now()) {
      return "update_window_closed";
    }
  }

  if (bundleStatus === "closed") {
    return "closed_to_new_students";
  }

  return "active";
};

export const getStudentOfferNextActionKind = (summary: StudentOfferSummary | StudentOfferSummarySeed): StudentOfferNextActionKind => {
  if (summary.readyQrCount > 0) return "open_qr_ready";
  if (summary.newCount > 0) return "open_new_files";
  if (summary.deliveredCount > 0) return "open_offer";
  return "track_delivery";
};

export const getStudentOfferPriority = (summary: StudentOfferSummary | StudentOfferSummarySeed): StudentOfferPriority => {
  if (summary.readyQrCount > 0 || summary.newCount > 0) return "use_first";
  if (summary.deliveredCount > 0) return "ready_now";
  if ((typeof summary.remainingCount === "number" && summary.remainingCount > 0) || summary.status === "active") {
    return "still_delivering";
  }
  return "settled";
};

export const getStudentOfferPriorityLabel = (summary: StudentOfferSummary): string => {
  if (summary.readyQrCount > 0) {
    return summary.readyQrCount === 1 ? "QR ready" : `${summary.readyQrCount} QR ready`;
  }
  if (summary.newCount > 0) {
    return summary.newCount === 1 ? "Use first" : `${summary.newCount} new`;
  }
  if (summary.priority === "ready_now") return "Ready now";
  if (summary.priority === "still_delivering") return "Still delivering";
  return "Settled";
};

export const getStudentOfferPriorityTone = (summary: StudentOfferSummary): "warm" | "success" | "brand" | "neutral" => {
  if (summary.readyQrCount > 0) return "warm";
  if (summary.priority === "use_first") return "success";
  if (summary.priority === "ready_now") return "brand";
  return "neutral";
};

export const getStudentOfferPrimaryActionLabel = (summary: StudentOfferSummary): string => {
  if (summary.nextActionKind === "open_qr_ready") return "Use QR-ready files";
  if (summary.nextActionKind === "open_new_files") return "Open new files";
  if (summary.nextActionKind === "open_offer") return "Open offer";
  return "Track delivery";
};

export const getStudentOfferDeliveryLabel = (summary: StudentOfferSummary): string => {
  const deliveredCount = Number(summary.deliveredCount ?? 0);
  return typeof summary.promiseCount === "number"
    ? `${deliveredCount} of ${summary.promiseCount} files`
    : `${deliveredCount} files ready`;
};

export const getStudentOfferTrustLine = (summary: StudentOfferSummary): string => {
  if (summary.status === "update_window_closed") {
    return "Your access stays yours. This offer is no longer expected to receive new files.";
  }

  if (summary.status === "closed_to_new_students") {
    return "This offer still belongs to you even though it is closed to new students.";
  }

  if (summary.deliveredCount === 0 && typeof summary.remainingCount === "number" && summary.remainingCount > 0) {
    return "This offer is active. The first files will appear here as delivery starts.";
  }

  if (typeof summary.remainingCount === "number" && summary.remainingCount > 0) {
    return `${summary.remainingCount} file${summary.remainingCount === 1 ? "" : "s"} still expected. Everything already delivered stays available.`;
  }

  if (summary.deliveredCount > 0) {
    return "Everything currently promised in this offer is already delivered.";
  }

  return "This offer stays available in your account once it is unlocked.";
};

export const getStudentOfferNextStepLine = (summary: StudentOfferSummary): string => {
  if (summary.readyQrCount > 0) {
    return `${summary.readyQrCount} QR-enabled file${summary.readyQrCount === 1 ? " is" : "s are"} ready to use from this offer now.`;
  }

  if (summary.newCount > 0) {
    return "Start with the newest file before returning to older material.";
  }

  if (summary.deliveredCount > 0) {
    return "Open this offer and continue from the files already ready.";
  }

  return "Keep this offer saved and check here as delivery continues.";
};

const compareStudentOfferSummaries = (a: StudentOfferSummary, b: StudentOfferSummary) => {
  const priorityRank: Record<StudentOfferPriority, number> = {
    use_first: 0,
    ready_now: 1,
    still_delivering: 2,
    settled: 3,
  };

  const priorityDelta = priorityRank[a.priority] - priorityRank[b.priority];
  if (priorityDelta !== 0) return priorityDelta;

  if (a.readyQrCount !== b.readyQrCount) return b.readyQrCount - a.readyQrCount;
  if (a.newCount !== b.newCount) return b.newCount - a.newCount;
  if (a.deliveredCount !== b.deliveredCount) return b.deliveredCount - a.deliveredCount;

  const aRemaining = typeof a.remainingCount === "number" ? a.remainingCount : -1;
  const bRemaining = typeof b.remainingCount === "number" ? b.remainingCount : -1;
  if (aRemaining !== bRemaining) return bRemaining - aRemaining;

  return new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime();
};

export const sortStudentOfferSummaries = (summaries: StudentOfferSummary[]): StudentOfferSummary[] =>
  [...summaries].sort(compareStudentOfferSummaries);

export const groupStudentOfferSummaries = (summaries: StudentOfferSummary[]): StudentOfferGroups => ({
  useFirst: summaries.filter((summary) => summary.priority === "use_first"),
  readyNow: summaries.filter((summary) => summary.priority === "ready_now"),
  stillDelivering: summaries.filter((summary) => summary.priority === "still_delivering"),
  settled: summaries.filter((summary) => summary.priority === "settled"),
});

const buildSummary = (
  purchase: BundlePurchaseRow,
  rows: BundleResourcesRow[],
  grantsByResourceId: Map<string, GrantQrRow>,
): StudentOfferSummary => {
  const bundle = toArrayFirst(purchase.bundles);
  const library = toArrayFirst(bundle?.libraries);
  const sortedRows = [...rows].sort((a, b) => {
    const aMs = a.added_at ? new Date(a.added_at).getTime() : 0;
    const bMs = b.added_at ? new Date(b.added_at).getTime() : 0;
    return bMs - aMs;
  });

  const deliveredCount = sortedRows.length;
  const recentRows = sortedRows.filter((row) => {
    if (!row.added_at) return false;
    return Date.now() - new Date(row.added_at).getTime() <= OFFER_NEW_WINDOW_MS;
  });
  const promiseCount = bundle?.promise_count ?? null;
  const qrResourceCount = rows.filter((row) => Boolean(toArrayFirst(row.resources)?.includes_print_qr_code)).length;
  const readyQrCount = rows.filter((row) => {
    const resourceId = row.resource_id ?? toArrayFirst(row.resources)?.id ?? "";
    if (!resourceId) return false;
    return Boolean(grantsByResourceId.get(String(resourceId))?.qr_token);
  }).length;
  const status = normalizeOfferStatus(bundle?.status, bundle?.ends_at);

  const summaryBase: StudentOfferSummarySeed = {
    purchaseId: purchase.id,
    offerId: purchase.bundle_id,
    title: bundle?.title ?? "Offer",
    libraryId: bundle?.library_id ?? "",
    libraryName: library?.name ?? null,
    libraryUsername: library?.username ?? null,
    purchasedAt: purchase.purchased_at,
    price: Number(bundle?.price ?? 0),
    currency: bundle?.currency ?? "EGP",
    periodLabel: bundle?.period_label ?? null,
    startsAt: bundle?.starts_at ?? null,
    endsAt: bundle?.ends_at ?? null,
    promiseCount,
    deliveredCount,
    remainingCount: typeof promiseCount === "number" ? Math.max(0, promiseCount - deliveredCount) : null,
    newCount: recentRows.length,
    qrResourceCount,
    readyQrCount,
    recentResourceIds: recentRows.slice(0, 3).map((row) => String(row.resource_id)).filter(Boolean),
    status,
  };

  return {
    ...summaryBase,
    priority: getStudentOfferPriority(summaryBase),
    nextActionKind: getStudentOfferNextActionKind(summaryBase),
  };
};

export const getStudentOfferUpdateLabel = (summary: StudentOfferSummary): string => {
  if (summary.status === "update_window_closed") return "Update window closed";
  if (summary.status === "closed_to_new_students") return "Closed to new students";
  if (summary.deliveredCount === 0 && typeof summary.remainingCount === "number" && summary.remainingCount > 0) {
    return "Waiting for first files";
  }
  if (typeof summary.remainingCount === "number" && summary.remainingCount > 0) {
    if (summary.endsAt) {
      return `Still delivering until ${new Date(summary.endsAt).toLocaleDateString()}`;
    }
    return `${summary.remainingCount} file${summary.remainingCount === 1 ? "" : "s"} still expected`;
  }
  if (summary.deliveredCount > 0) return "Everything promised is already delivered";
  if (summary.endsAt) {
    return `Updates expected until ${new Date(summary.endsAt).toLocaleDateString()}`;
  }
  if (summary.periodLabel) return summary.periodLabel;
  return "Offer access";
};

export async function fetchStudentPassSummaries(userId: string): Promise<StudentOfferSummary[]> {
  return fetchStudentOfferSummaries(userId);
}

export async function fetchStudentOfferSummaries(userId: string): Promise<StudentOfferSummary[]> {
  const { data: purchaseRows, error: purchaseError } = await supabase
    .from("bundle_purchases")
    .select("id,bundle_id,purchased_at,bundles(id,title,price,currency,promise_count,period_label,starts_at,ends_at,status,library_id,libraries(name,username))")
    .eq("student_id", userId)
    .order("purchased_at", { ascending: false });

  if (purchaseError) throw purchaseError;

  const purchases = ((purchaseRows as BundlePurchaseRow[] | null) ?? []).filter((row) => row.bundle_id);
  if (!purchases.length) return [];

  const bundleIds = purchases.map((row) => row.bundle_id);
  const { data: bundleResourceRows, error: resourceError } = await supabase
    .from("bundle_resources")
    .select("bundle_id,added_at,resource_id,resources(includes_print_qr_code)")
    .in("bundle_id", bundleIds)
    .order("added_at", { ascending: false });

  if (resourceError) throw resourceError;

  const resourceIds = Array.from(
    new Set(
      ((bundleResourceRows as BundleResourcesRow[] | null) ?? [])
        .map((row) => row.resource_id)
        .filter((resourceId): resourceId is string => Boolean(resourceId)),
    ),
  );

  let grantsByCompositeKey = new Map<string, GrantQrRow>();
  if (resourceIds.length) {
    const { data: grantRows, error: grantError } = await supabase
      .from("resource_access_grants")
      .select("resource_id,source_bundle_id,qr_token,qr_redeemed_at")
      .eq("user_id", userId)
      .in("resource_id", resourceIds)
      .in("source_bundle_id", bundleIds);

    if (grantError) throw grantError;

    grantsByCompositeKey = new Map(
      ((grantRows as GrantQrRow[] | null) ?? []).map((row) => [`${row.source_bundle_id}:${row.resource_id}`, row]),
    );
  }

  const rowsByBundle = new Map<string, BundleResourcesRow[]>();
  ((bundleResourceRows as BundleResourcesRow[] | null) ?? []).forEach((row) => {
    const bundleId = row.bundle_id ?? "";
    if (!bundleId) return;
    const current = rowsByBundle.get(bundleId) ?? [];
    current.push(row);
    rowsByBundle.set(bundleId, current);
  });

  const summaries = purchases.map((purchase) => {
    const rows = rowsByBundle.get(purchase.bundle_id) ?? [];
    const grantMapForBundle = new Map<string, GrantQrRow>();
    rows.forEach((row) => {
      const resourceId = row.resource_id;
      if (!resourceId) return;
      const grant = grantsByCompositeKey.get(`${purchase.bundle_id}:${resourceId}`);
      if (grant) grantMapForBundle.set(resourceId, grant);
    });
    return buildSummary(purchase, rows, grantMapForBundle);
  });

  return sortStudentOfferSummaries(summaries);
}

export async function fetchStudentOfferDetail(userId: string, offerId: string): Promise<StudentOfferDetail | null> {
  const { data: purchaseRow, error: purchaseError } = await supabase
    .from("bundle_purchases")
    .select("id,bundle_id,purchased_at,bundles(id,title,price,currency,promise_count,period_label,starts_at,ends_at,status,description,offer_headline,what_coming_later,bonus_items,library_id,libraries(name,username))")
    .eq("student_id", userId)
    .eq("bundle_id", offerId)
    .maybeSingle();

  if (purchaseError) throw purchaseError;
  if (!purchaseRow) return null;

  const purchase = purchaseRow as BundlePurchaseRow;

  const { data: bundleResourceRows, error: resourceError } = await supabase
    .from("bundle_resources")
    .select("bundle_id,added_at,is_bonus,resource_id,resources(id,title,target_faculty,target_year,thumb_key,format,includes_print_qr_code,library:libraries(name,username))")
    .eq("bundle_id", offerId)
    .order("added_at", { ascending: false });

  if (resourceError) throw resourceError;

  const passResourceIds = Array.from(
    new Set(
      ((bundleResourceRows as BundleResourcesRow[] | null) ?? [])
        .map((row) => row.resource_id)
        .filter((resourceId): resourceId is string => Boolean(resourceId)),
    ),
  );

  let grantByResourceId = new Map<string, GrantQrRow>();
  if (passResourceIds.length) {
    const { data: grantRows, error: grantError } = await supabase
      .from("resource_access_grants")
      .select("resource_id,source_bundle_id,qr_token,qr_redeemed_at")
      .eq("user_id", userId)
      .eq("source_bundle_id", offerId)
      .in("resource_id", passResourceIds);

    if (grantError) throw grantError;

    grantByResourceId = new Map(
      ((grantRows as GrantQrRow[] | null) ?? []).map((row) => [row.resource_id, row]),
    );
  }

  const resources = ((bundleResourceRows as BundleResourcesRow[] | null) ?? []).map((row) => {
    const resource = toArrayFirst(row.resources);
    const library = toArrayFirst(resource?.library);
    const grant = grantByResourceId.get(String(row.resource_id ?? resource?.id ?? ""));
    return {
      resourceId: String(row.resource_id ?? resource?.id ?? ""),
      title: resource?.title ?? "Resource",
      category: resource?.target_faculty ?? null,
      yearLevel: resource?.target_year ?? null,
      addedAt: row.added_at ?? new Date(0).toISOString(),
      isBonus: Boolean(row.is_bonus),
      isNew: Boolean(row.added_at) && Date.now() - new Date(String(row.added_at)).getTime() <= OFFER_NEW_WINDOW_MS,
      thumbKey: resource?.thumb_key ?? null,
      format: resource?.format ?? null,
      includesPrintQrCode: Boolean(resource?.includes_print_qr_code),
      qrToken: grant?.qr_token ?? null,
      qrRedeemedAt: grant?.qr_redeemed_at ?? null,
      libraryName: library?.name ?? null,
      libraryUsername: library?.username ?? null,
    } satisfies StudentOfferResource;
  }).filter((item) => item.resourceId);

  const { data: requestRow, error: requestError } = await supabase
    .from("access_requests")
    .select("id,request_code,status")
    .eq("student_id", userId)
    .eq("bundle_id", offerId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (requestError) throw requestError;

  const summary = buildSummary(purchase, (bundleResourceRows as BundleResourcesRow[] | null) ?? [], grantByResourceId);
  const bundle = toArrayFirst(purchase.bundles);

  return {
    summary,
    description: bundle?.description ?? null,
    offerHeadline: bundle?.offer_headline ?? null,
    whatComingLater: bundle?.what_coming_later ?? [],
    bonusItems: bundle?.bonus_items ?? [],
    requestId: (requestRow as AccessRequestRow | null)?.id ?? null,
    requestCode: (requestRow as AccessRequestRow | null)?.request_code ?? null,
    requestStatus: (requestRow as AccessRequestRow | null)?.status ?? null,
    resources,
  };
}

export async function fetchStudentOfferOwnershipByResourceIds(
  userId: string,
  resourceIds: string[],
): Promise<Record<string, StudentOfferOwnership>> {
  const uniqueResourceIds = Array.from(new Set(resourceIds.filter(Boolean)));
  if (!uniqueResourceIds.length) return {};

  const summaries = await fetchStudentOfferSummaries(userId);
  if (!summaries.length) return {};

  const passMap = new Map(summaries.map((summary) => [summary.offerId, summary]));

  const { data: bundleResourceRows, error: resourceError } = await supabase
    .from("bundle_resources")
    .select("bundle_id,resource_id")
    .in("bundle_id", summaries.map((summary) => summary.offerId))
    .in("resource_id", uniqueResourceIds);

  if (resourceError) throw resourceError;

  const ownershipByResource: Record<string, StudentOfferOwnership> = {};
  ((bundleResourceRows as Array<{ bundle_id: string | null; resource_id: string | null }> | null) ?? []).forEach((row) => {
    if (!row.resource_id || !row.bundle_id) return;
    const summary = passMap.get(row.bundle_id);
    if (!summary) return;

    const existing = ownershipByResource[row.resource_id];
    if (existing && new Date(existing.purchasedAt).getTime() >= new Date(summary.purchasedAt).getTime()) {
      return;
    }

    ownershipByResource[row.resource_id] = {
      resourceId: row.resource_id,
      offerId: summary.offerId,
      title: summary.title,
      purchasedAt: summary.purchasedAt,
      libraryName: summary.libraryName,
      libraryUsername: summary.libraryUsername,
      deliveredCount: summary.deliveredCount,
      promiseCount: summary.promiseCount,
      remainingCount: summary.remainingCount,
      newCount: summary.newCount,
      status: summary.status,
      owned: true,
    };
  });

  return ownershipByResource;
}

export async function fetchBundleDiscoveryDetail(bundleId: string) {
  const { data: bundleRow, error: bundleError } = await supabase
    .from("bundles")
    .select(`
      id, 
      title, 
      description, 
      price, 
      currency, 
      status,
      offer_headline, 
      promise_count, 
      target_university, 
      target_faculty,
      what_coming_later, 
      bonus_items,
      library:libraries(
        id, 
        name, 
        username, 
        description,
        owner:profiles!libraries_owner_id_fkey(avatar_url)
      )
    `)
    .eq("id", bundleId)
    .maybeSingle();

  if (bundleError) throw bundleError;
  if (!bundleRow) return null;

  const { data: resourceRows, error: resourceError } = await supabase
    .from("bundle_resources")
    .select(`
      added_at,
      is_bonus,
      resource:resources(
        id, 
        title, 
        target_faculty, 
        target_year, 
        thumb_key, 
        format,
        library:libraries(name, username)
      )
    `)
    .eq("bundle_id", bundleId)
    .order("added_at", { ascending: false });

  if (resourceError) throw resourceError;

  const bundle = bundleRow as any;
  const library = toArrayFirst(bundle.library);

  return {
    id: bundle.id,
    title: bundle.title,
    description: bundle.description,
    price: bundle.price,
    currency: bundle.currency,
    status: bundle.status,
    offerHeadline: bundle.offer_headline,
    promiseCount: bundle.promise_count,
    targetUniversity: bundle.target_university,
    targetFaculty: bundle.target_faculty,
    whatComingLater: bundle.what_coming_later || [],
    bonusItems: bundle.bonus_items || [],
    libraryName: library?.name || null,
    libraryUsername: library?.username || null,
    libraryDescription: library?.description || null,
    libraryAvatar: library?.owner?.avatar_url || null,
    resources: (resourceRows ?? []).map((row: any) => {
      const res = toArrayFirst(row.resource);
      const resLib = toArrayFirst((res as any)?.library);
      return {
        id: res?.id,
        title: res?.title || "Untitled",
        category: res?.target_faculty,
        targetYear: res?.target_year,
        thumbKey: res?.thumb_key,
        format: res?.format,
        addedAt: row.added_at,
        isBonus: row.is_bonus,
        libraryName: resLib?.name,
        libraryUsername: resLib?.username
      };
    }).filter((r: any) => r.id)
  };
}
